/* eslint-disable */
/**
 * @fileoverview Custom hook for dashboard data fetching
 *
 * Centralizes all dashboard data fetching logic including:
 * - User data and bookmarked clients
 * - Activities
 * - Invoices
 * - Dashboard statistics
 * - Time entries
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getGlobalTracker } from '../../../utils/ActivityTracker';
import { debugLog, debugWarn, debugError } from '../../../utils/debug';

/**
 * Custom hook for fetching and managing dashboard data
 *
 * @returns {Object} Dashboard data and loading states
 */
export const useDashboardData = () => {
    const { getUser, hasPermission, apiRequest, getCookie } = useAuth();
    const navigate = useNavigate();

    // State management
    const [user, setUser] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [bookmarksLoading, setBookmarksLoading] = useState(true);
    const [bookmarkedClients, setBookmarkedClients] = useState([]);
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const [permissions, setPermissions] = useState({});
    const [stats, setStats] = useState({
        todayHours: 0,
        weekHours: 0,
        monthRevenue: 0,
        activeClients: 0,
        pendingInvoices: 0,
        completedTasks: 0
    });
    const [recentTimeEntries, setRecentTimeEntries] = useState([]);

    // Activity tracker helper - example of custom event tracking
    const trackCustomEvent = useCallback((eventName, eventData = {}) => {
        const tracker = getGlobalTracker();
        if (tracker) {
            tracker.logSystemEvent(eventName, eventData);
        }
    }, []);

    // Fetch user bookmarked clients
    const fetchUserData = useCallback(async () => {
        setBookmarksLoading(true);
        try {
            const response = await apiRequest('/user');

            if (response.ok) {
                const data = await response.json();
                if (data.bookmarked_clients) {
                    setBookmarkedClients(data.bookmarked_clients);
                }
            } else {
                debugError('Failed to fetch user data');
            }
        } catch (e) {
            debugError('Error fetching user data:', e);
        } finally {
            setBookmarksLoading(false);
        }
    }, [apiRequest]);

    // Fetch activities (filtered for legacy actions only)
    const fetchActivities = useCallback(async () => {
        try {
            const response = await apiRequest('/activities');

            if (response.ok) {
                const data = await response.json();

                // Filter out system events (created/updated/deleted actions)
                // Keep only custom actions and legacy events
                const legacyActions = data.filter(activity => {
                    const action = activity.action ? activity.action.toLowerCase() : '';

                    // Exclude system CRUD operations
                    if (action.includes('created') || action.includes('updated') || action.includes('deleted')) {
                        return false;
                    }

                    return true;
                });

                setActivities(legacyActions.slice(0, 10)); // Show only 10 most recent
            } else {
                debugError('Failed to fetch activities');
            }
        } catch (e) {
            debugError('Error fetching activities:', e);
        }
    }, [apiRequest]);

    // Fetch dashboard stats
    const fetchDashboardStats = useCallback(async () => {
        try {
            // Track dashboard stats view - example of custom event tracking
            trackCustomEvent('dashboard_stats_loaded', {
                action: 'view_dashboard_stats',
                timestamp: new Date().toISOString()
            });

            // Simulate fetching stats - replace with actual API calls
            const timeEntriesResponse = await apiRequest('/time_entries?per_page=100');
            const clientsResponse = await apiRequest('/clients');

            if (timeEntriesResponse.ok) {
                const data = await timeEntriesResponse.json();
                const entries = data.items || [];
                const today = new Date();
                const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

                // Calculate stats from time entries
                const todayEntries = entries.filter(e => new Date(e.created_at).toDateString() === today.toDateString());
                const weekEntries = entries.filter(e => new Date(e.created_at) >= weekAgo);
                const recentEntries = entries.slice(0, 5);

                setRecentTimeEntries(recentEntries);

                // Calculate hours
                const todayHours = todayEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
                const weekHours = weekEntries.reduce((sum, e) => sum + (e.hours || 0), 0);

                setStats(prev => ({
                    ...prev,
                    todayHours: todayHours.toFixed(1),
                    weekHours: weekHours.toFixed(1),
                    completedTasks: entries.filter(e => e.status === 'completed').length
                }));
            }

            if (clientsResponse.ok) {
                const clients = await clientsResponse.json();
                setStats(prev => ({
                    ...prev,
                    activeClients: clients.filter(c => c.status === 'active').length
                }));
            }

            // Set pending invoices from already fetched invoices
            setStats(prev => ({
                ...prev,
                pendingInvoices: invoices.filter(i => i.status === 'pending').length,
                monthRevenue: invoices.reduce((sum, i) => sum + (i.total || 0), 0)
            }));

        } catch (e) {
            debugError('Error fetching dashboard stats:', e);
        }
    }, [apiRequest, invoices, trackCustomEvent]);

    // Fetch invoices
    const fetchInvoices = useCallback(async () => {
        try {
            const response = await apiRequest('/invoices');

            if (response.ok) {
                const data = await response.json();
                setInvoices(data);
            } else {
                debugError('Error fetching invoices');
            }
        } catch (e) {
            debugError('Error fetching invoices:', e);
        }
    }, [apiRequest]);

    // Load user and permissions on component mount
    useEffect(() => {
        const loadUserAndPermissions = async () => {
            try {
                const csrfToken = getCookie('csrf_access_token');
                if (!csrfToken) {
                    debugLog('Warning: Dashboard: No CSRF token found, skipping data load');
                    return;
                }

                setUserLoading(true);

                // Get fresh user data
                const userData = await getUser({});
                if (!userData) {
                    debugLog('Warning: Dashboard: No user data, redirecting to login');
                    navigate('/login');
                    return;
                }

                setUser(userData);

                fetchUserData()
                fetchActivities()

                // Load permissions we need for the dashboard
                const permissionChecks = await Promise.all([
                    hasPermission('can_view_clients'),
                    hasPermission('can_view_invoices'),
                    hasPermission('can_view_time_entries'),
                    hasPermission('can_view_billing'),
                    hasPermission('can_view_payments'),
                    hasPermission('can_view_ar_ledger'),
                    hasPermission('can_view_reports_page')
                ]);

                setPermissions({
                    canViewClients: permissionChecks[0],
                    canViewInvoices: permissionChecks[1],
                    canViewTimeEntries: permissionChecks[2],
                    canViewBilling: permissionChecks[3],
                    canViewPayments: permissionChecks[4],
                    canViewArLedger: permissionChecks[5],
                    canViewReports: permissionChecks[6]
                });


            } catch (err) {
                debugError('Dashboard: Failed to load user data:', err);
                // Don't navigate to login if it's just a network error
                if (err.message && err.message.includes('401')) {
                    navigate('/login');
                }
            } finally {
                setUserLoading(false);
            }
        };

        loadUserAndPermissions();
    }, [getCookie, getUser, hasPermission, navigate, fetchUserData, fetchActivities]);

    // Load dashboard data based on permissions
    useEffect(() => {
        if (!user) return;

        const loadDashboardData = async () => {
            setLoading(true);

            try {
                // Load bookmarked clients if user can view clients
                if (permissions.canViewClients) {
                    await fetchUserData();
                }

                // Load activities (available to all users)
                await fetchActivities();

                // Load invoices if user can view them
                if (permissions.canViewInvoices) {
                    await fetchInvoices();
                }

                // Load dashboard stats
                await fetchDashboardStats();

            } catch (err) {
                debugError('Error loading dashboard data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (Object.keys(permissions).length > 0) {
            loadDashboardData();
        }
    }, []);

    // Update current time every minute
    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 60000);

        return () => clearInterval(intervalId);
    }, []);

    // Listen for bookmark updates from other components
    useEffect(() => {
        const handleBookmarkUpdate = (event) => {
            fetchUserData();

            // Add visual feedback by animating the updated bookmark card
            if (event.detail && event.detail.clientId) {
                setTimeout(() => {
                    const bookmarkCard = document.querySelector(`[data-client-id="${event.detail.clientId}"]`);
                    if (bookmarkCard) {
                        bookmarkCard.classList.add('bookmark-updated');
                        setTimeout(() => {
                            bookmarkCard.classList.remove('bookmark-updated');
                        }, 600);
                    }
                }, 100); // Small delay to ensure DOM is updated
            }
        };

        window.addEventListener('bookmarkUpdated', handleBookmarkUpdate);
        return () => {
            window.removeEventListener('bookmarkUpdated', handleBookmarkUpdate);
        };
    }, [fetchUserData]);

    // Get user role display name
    const getUserRoleDisplay = useCallback(() => {
        if (!user) return '';
        if (user.role && user.role.name) return user.role.name;
        if (user.role_name) return user.role_name;
        if (user.role) return user.role; // Legacy fallback
        return 'User';
    }, [user]);

    return {
        // State
        user,
        userLoading,
        activities,
        invoices,
        loading,
        bookmarksLoading,
        bookmarkedClients,
        currentDateTime,
        permissions,
        stats,
        recentTimeEntries,

        // Actions
        fetchUserData,
        fetchActivities,
        fetchDashboardStats,
        fetchInvoices,
        trackCustomEvent,
        getUserRoleDisplay,

        // Navigation
        navigate
    };
};

export default useDashboardData;
